import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, SkeletonText } from "@carbon/react";
import { PieChart } from "@carbon/charts-react";
import {
  CheckmarkOutline,
  Hospital,
  Report,
  Renew,
  Security,
} from "@carbon/react/icons";
import "@carbon/charts/styles.css";
import "./Metrics.css";
import { FacilityDataTableComponent } from "../facility-data-table.component";
import { PoliceDataTableComponent } from "../police-data-table.component";
import { pieChartReportOptions, pieChartRDEPOCOptions } from "../constants";
import StackedBarChartByAge from "./StackedBarChartByAge";
import DashboardInsights, { buildDashboardAnalysis } from "./DashboardInsights";

const formatNumber = value => value.toLocaleString();
const formatPercent = value => `${value.toFixed(1)}%`;

const MetricCard = ({ label, value, note, icon: Icon, tone }) => (
  <article className={`metric-card metric-card-${tone}`}>
    <div className="metric-card-top">
      <span className="metric-card-label">{label}</span>
      <span className="metric-card-icon"><Icon size={22} /></span>
    </div>
    <strong className="metric-card-value">{formatNumber(value)}</strong>
    <span className="metric-card-note">{note}</span>
  </article>
);

const MetricsLoading = () => (
  <div className="dashboard-loading" aria-label="Loading dashboard data">
    {[0, 1, 2, 3].map(item => (
      <div className="metric-card loading-card" key={item}>
        <SkeletonText width="45%" />
        <SkeletonText heading width="65%" />
        <SkeletonText width="80%" />
      </div>
    ))}
  </div>
);

const Metrics = ({ district, dateArray, config = {} }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestKey, setRequestKey] = useState(0);

  const isAllDistricts = district?.trim().toUpperCase() === "ALL";
  const dateFrom = dateArray?.[0]?.toISOString().split("T")[0];
  const dateTo = dateArray?.[1]?.toISOString().split("T")[0];

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          "https://sgbv.caringsoulshomecare.com/api/",
          {
            params: isAllDistricts
              ? { cmd: "getDashboardData_1" }
              : {
                  cmd: "getDashboardData",
                  district,
                  date_from: dateFrom,
                  date_to: dateTo,
                },
            signal: controller.signal,
          }
        );

        if (response.data.status !== "ok") {
          throw new Error(response.data.message || "No dashboard data available");
        }

        const results = Array.isArray(response.data.results)
          ? response.data.results[0]
          : response.data.results;

        if (!results) {
          throw new Error("No dashboard data available");
        }

        setDashboardData(results);
      } catch (requestError) {
        if (!axios.isCancel(requestError)) {
          setDashboardData(null);
          setError(requestError);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    if (district && dateFrom && dateTo) {
      fetchData();
    }

    return () => controller.abort();
  }, [district, dateFrom, dateTo, isAllDistricts, requestKey]);

  const analysis = useMemo(
    () => buildDashboardAnalysis(dashboardData),
    [dashboardData]
  );

  if (loading) {
    return <MetricsLoading />;
  }

  if (error) {
    return (
      <div className="dashboard-state error-state" role="alert">
        <span className="state-icon"><Report size={28} /></span>
        <div><h2>Dashboard data could not be loaded</h2><p>{error.message}</p></div>
        <Button kind="tertiary" renderIcon={Renew} onClick={() => setRequestKey(key => key + 1)}>Try again</Button>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="dashboard-state"><p>No dashboard data is available for this selection.</p></div>;
  }

  const metricCards = [
    {
      label: "Cases reported",
      value: analysis.total,
      note: isAllDistricts ? "Across all reporting districts" : `Reported in ${district}`,
      icon: Report,
      tone: "primary",
    },
    {
      label: "Cases completed",
      value: analysis.completed,
      note: `${formatPercent(analysis.completionRate)} of reported cases`,
      icon: CheckmarkOutline,
      tone: "success",
    },
    {
      label: "Health-centre cases",
      value: analysis.health,
      note: `${formatPercent(analysis.healthShare)} of reporting-channel cases`,
      icon: Hospital,
      tone: "health",
    },
    {
      label: "Police-station cases",
      value: analysis.police,
      note: `${formatPercent(analysis.policeShare)} of reporting-channel cases`,
      icon: Security,
      tone: "police",
    },
  ];

  return (
    <div className="dashboard-content">
      <section className="metrics-section" aria-labelledby="key-metrics-title">
        <div className="section-heading compact-heading">
          <div>
            <span className="section-kicker">At a glance</span>
            <h2 id="key-metrics-title">Key response metrics</h2>
          </div>
          <span className="section-period">{isAllDistricts ? "All available dates" : `${dateFrom} — ${dateTo}`}</span>
        </div>
        <div className="metric-tile-grid">
          {metricCards.map(card => <MetricCard key={card.label} {...card} />)}
        </div>
      </section>

      {config.showDemographics !== false && <section className="analytics-section" aria-labelledby="analytics-title">
        <div className="section-heading">
          <div>
            <span className="section-kicker">Demographics and channels</span>
            <h2 id="analytics-title">Case distribution</h2>
            <p>Understand who is represented and where reports are recorded.</p>
          </div>
        </div>

        <div className="analytics-grid">
          <article className="chart-card chart-card-small">
            <div className="chart-card-heading"><h3>Gender distribution</h3><span>{formatNumber(analysis.female + analysis.male)} records</span></div>
            <PieChart
              options={pieChartRDEPOCOptions}
              data={[
                { group: "Male", value: analysis.male },
                { group: "Female", value: analysis.female },
              ]}
            />
          </article>

          <article className="chart-card chart-card-wide">
            <div className="chart-card-heading"><h3>Age distribution</h3><span>{formatNumber(analysis.knownAgeTotal)} classified records</span></div>
            <StackedBarChartByAge dashboardData={dashboardData} />
          </article>

          <article className="chart-card chart-card-small">
            <div className="chart-card-heading"><h3>Reporting channels</h3><span>{formatNumber(analysis.health + analysis.police)} cases</span></div>
            <PieChart
              options={pieChartReportOptions}
              data={[
                { group: "Police station", value: analysis.police },
                { group: "Health centre", value: analysis.health },
              ]}
            />
          </article>
        </div>
      </section>}

      {config.showAiInsights !== false && <DashboardInsights dashboardData={dashboardData} district={district} />}

      {config.showServiceTables !== false && <section className="services-section" aria-labelledby="services-title">
        <div className="section-heading">
          <div>
            <span className="section-kicker">Response services</span>
            <h2 id="services-title">Service delivery by reporting station</h2>
            <p>Search and compare services recorded by facilities and police stations.</p>
          </div>
        </div>

        <div className="services-grid">
          <article className="table-card">
            <div className="table-card-heading"><span className="table-card-icon health-icon"><Hospital size={20} /></span><div><h3>Health facilities</h3><p>Clinical and psychosocial services</p></div></div>
            <FacilityDataTableComponent district={district} dateFrom={dateFrom} dateTo={dateTo} />
          </article>
          <article className="table-card">
            <div className="table-card-heading"><span className="table-card-icon police-icon"><Security size={20} /></span><div><h3>Police stations</h3><p>Investigation and justice services</p></div></div>
            <PoliceDataTableComponent district={district} dateFrom={dateFrom} dateTo={dateTo} />
          </article>
        </div>
      </section>}
    </div>
  );
};

export default Metrics;
